import React, { useEffect, useState } from 'react'
import { Alert, Platform, TextInput } from 'react-native'
import { ArrowLeft, Plus, Mail, Phone, MapPin, CreditCard, Trash2, Building2, X } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  Button, ButtonText, Sheet, Spinner, Text,
  XStack, YStack, ScrollView,
} from '@fliisi/pollen'
import { FullScreenLayout } from '../components/ScreenLayout'

const merchantColors = { primary: '#C07A5F', primaryLight: '#D4A088', accent: '#748C70' }

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'SupplierManagement'>
  route: RouteProp<RootStackParamList, 'SupplierManagement'>
}

interface Supplier {
  id: string; name: string; contact_name: string | null; email: string | null; phone: string | null
  address_line1: string | null; city: string | null; postal_code: string | null
  business_id: string | null; payment_terms: string | null; notes: string | null
  is_active: boolean; created_at: string; product_count?: number
}

const initialFormState = {
  name: '', contact_name: '', email: '', phone: '', address_line1: '', address_line2: '',
  city: '', postal_code: '', business_id: '', payment_terms: '', notes: '',
}

export default function SupplierManagementScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { t } = useLanguage()
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null)
  const [form, setForm] = useState(initialFormState)
  const [saving, setSaving] = useState(false)

  useEffect(() => { fetchSuppliers() }, [storeId])

  const fetchSuppliers = async () => {
    try {
      const { data, error } = await supabase.from('suppliers').select('*').eq('flea_market_id', storeId).order('name')
      if (error) throw error
      const { data: productCounts } = await supabase.from('products').select('supplier_id').eq('flea_market_id', storeId).not('supplier_id', 'is', null)
      const countMap: Record<string, number> = {}
      productCounts?.forEach(p => { countMap[p.supplier_id] = (countMap[p.supplier_id] || 0) + 1 })
      setSuppliers((data || []).map(s => ({ ...s, product_count: countMap[s.id] || 0 })))
    } catch (error) { console.error('Error fetching suppliers:', error) }
    finally { setLoading(false); setRefreshing(false) }
  }

  const showAlert = (title: string, message: string) => {
    Platform.OS === 'web' ? window.alert(`${title}\n\n${message}`) : Alert.alert(title, message)
  }

  const confirmAction = (title: string, message: string, onConfirm: () => void) => {
    if (Platform.OS === 'web') { if (window.confirm(`${title}\n\n${message}`)) onConfirm() }
    else Alert.alert(title, message, [{ text: t('cancel') || 'Cancel', style: 'cancel' }, { text: t('confirm') || 'Confirm', onPress: onConfirm }])
  }

  const openAddModal = () => { setEditingSupplier(null); setForm(initialFormState); setModalVisible(true) }

  const openEditModal = (supplier: Supplier) => {
    setEditingSupplier(supplier)
    setForm({ name: supplier.name, contact_name: supplier.contact_name || '', email: supplier.email || '', phone: supplier.phone || '', address_line1: supplier.address_line1 || '', address_line2: '', city: supplier.city || '', postal_code: supplier.postal_code || '', business_id: supplier.business_id || '', payment_terms: supplier.payment_terms || '', notes: supplier.notes || '' })
    setModalVisible(true)
  }

  const saveSupplier = async () => {
    if (!form.name.trim()) { showAlert(t('error'), t('enter_supplier_name') || 'Please enter a supplier name'); return }
    setSaving(true)
    try {
      const supplierData = {
        flea_market_id: storeId, name: form.name.trim(), contact_name: form.contact_name.trim() || null,
        email: form.email.trim() || null, phone: form.phone.trim() || null, address_line1: form.address_line1.trim() || null,
        city: form.city.trim() || null, postal_code: form.postal_code.trim() || null, business_id: form.business_id.trim() || null,
        payment_terms: form.payment_terms.trim() || null, notes: form.notes.trim() || null,
      }
      if (editingSupplier) {
        const { error } = await supabase.from('suppliers').update(supplierData).eq('id', editingSupplier.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('suppliers').insert(supplierData)
        if (error) throw error
      }
      setModalVisible(false); fetchSuppliers()
      showAlert(t('success'), editingSupplier ? (t('supplier_updated') || 'Supplier updated!') : (t('supplier_created') || 'Supplier created!'))
    } catch (error: any) { console.error('Save error:', error); showAlert(t('error'), error.message || t('save_failed') || 'Failed to save') }
    finally { setSaving(false) }
  }

  const deleteSupplier = (supplier: Supplier) => {
    if (supplier.product_count && supplier.product_count > 0) {
      showAlert(t('error'), t('supplier_has_products') || `Cannot delete supplier. ${supplier.product_count} products are linked to this supplier.`)
      return
    }
    confirmAction(t('delete_supplier') || 'Delete Supplier', t('confirm_delete_supplier') || 'Are you sure you want to delete this supplier?', async () => {
      try { const { error } = await supabase.from('suppliers').delete().eq('id', supplier.id); if (error) throw error; fetchSuppliers() }
      catch (error: any) { console.error('Delete error:', error); showAlert(t('error'), error.message || t('delete_failed') || 'Failed to delete') }
    })
  }

  if (loading) return <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>

  return (
    <FullScreenLayout>
      {/* Header */}
      <XStack jc="space-between" ai="center" px="$md" py="$md" bg="$backgroundCard" bbw={1} bbc="$borderColor">
        <XStack ai="center" onPress={() => navigation.goBack()} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
          <ArrowLeft size={18} color={merchantColors.primary} />
          <Text color={merchantColors.primary} fontSize={16} fontWeight="500" ml={4}>{t('back') || 'Back'}</Text>
        </XStack>
        <Text fontSize={18} fontWeight="bold" color="$color">{t('suppliers') || 'Suppliers'}</Text>
        <XStack ai="center" onPress={openAddModal} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
          <Plus size={18} color={merchantColors.primary} />
          <Text color={merchantColors.primary} fontSize={16} fontWeight="600" ml={4}>{t('add') || 'Add'}</Text>
        </XStack>
      </XStack>

      {/* Suppliers List */}
      <ScrollView f={1} contentContainerStyle={{ padding: 16 }} refreshControl={<>{refreshing && <Spinner />}</>}>
        {suppliers.length === 0 ? (
          <YStack ai="center" py="$3xl" px="$xl">
            <YStack w={80} h={80} br="$full" bg={`${merchantColors.primary}15`} jc="center" ai="center" mb="$md">
              <Building2 size={48} color={merchantColors.primary} />
            </YStack>
            <Text fontSize={18} fontWeight="600" color="$color" mb="$sm">{t('no_suppliers') || 'No suppliers yet'}</Text>
            <Text fontSize={14} color="$colorSecondary" ta="center" mb="$lg">{t('add_supplier_hint') || 'Add suppliers to track where your products come from'}</Text>
            <Button variant="primary" onPress={openAddModal}><Plus size={16} color="#fff" /><ButtonText>{t('add_supplier') || 'Add Supplier'}</ButtonText></Button>
          </YStack>
        ) : (
          <YStack gap="$sm">
            {suppliers.map(item => (
              <YStack key={item.id} bg="$backgroundCard" br="$lg" p="$md" elevation={2} onPress={() => openEditModal(item)} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
                <XStack jc="space-between" ai="flex-start" mb="$sm">
                  <YStack>
                    <Text fontSize={18} fontWeight="600" color="$color">{item.name}</Text>
                    {item.contact_name && <Text fontSize={14} color="$colorSecondary" mt={2}>{item.contact_name}</Text>}
                  </YStack>
                  <YStack px={10} py={4} br="$full" bg={item.is_active ? '#4CAF50' : '#999'}>
                    <Text color="white" fontSize={12} fontWeight="600">{item.is_active ? (t('active') || 'Active') : (t('inactive') || 'Inactive')}</Text>
                  </YStack>
                </XStack>

                <YStack mb="$sm">
                  {item.email && <XStack ai="center" mb={6}><YStack w={20} mr="$sm" ai="center"><Mail size={14} color="#999" /></YStack><Text fontSize={14} color="$colorSecondary" f={1}>{item.email}</Text></XStack>}
                  {item.phone && <XStack ai="center" mb={6}><YStack w={20} mr="$sm" ai="center"><Phone size={14} color="#999" /></YStack><Text fontSize={14} color="$colorSecondary" f={1}>{item.phone}</Text></XStack>}
                  {(item.city || item.address_line1) && <XStack ai="center" mb={6}><YStack w={20} mr="$sm" ai="center"><MapPin size={14} color="#999" /></YStack><Text fontSize={14} color="$colorSecondary" f={1}>{[item.address_line1, item.city, item.postal_code].filter(Boolean).join(', ')}</Text></XStack>}
                  {item.payment_terms && <XStack ai="center" mb={6}><YStack w={20} mr="$sm" ai="center"><CreditCard size={14} color="#999" /></YStack><Text fontSize={14} color="$colorSecondary" f={1}>{item.payment_terms}</Text></XStack>}
                </YStack>

                <XStack jc="space-between" ai="center" pt="$sm" btw={1} btc="$borderColor">
                  <YStack bg={`${merchantColors.primary}15`} px={10} py={4} br="$sm">
                    <Text fontSize={13} color={merchantColors.primary} fontWeight="500">{item.product_count || 0} {t('products') || 'products'}</Text>
                  </YStack>
                  <YStack p={4} onPress={(e: any) => { e.stopPropagation(); deleteSupplier(item) }} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
                    <Trash2 size={18} color="#F44336" />
                  </YStack>
                </XStack>
              </YStack>
            ))}
          </YStack>
        )}
        <YStack h={100} />
      </ScrollView>

      {/* Add/Edit Modal */}
      <Sheet open={modalVisible} onOpenChange={setModalVisible} noHandle>
            <XStack jc="space-between" ai="center" p="$md" bbw={1} bbc="$borderColor">
              <Text fontSize={18} fontWeight="bold" color="$color">
                {editingSupplier ? (t('edit_supplier') || 'Edit Supplier') : (t('add_supplier') || 'Add Supplier')}
              </Text>
              <YStack p={4} onPress={() => setModalVisible(false)} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
                <X size={24} color="#999" />
              </YStack>
            </XStack>

            <ScrollView p="$md" maxHeight={450}>
              <Text fontSize={14} color="$colorSecondary" mb={4}>{t('supplier_name') || 'Supplier Name'} *</Text>
              <TextInput style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12 }} value={form.name} onChangeText={(v) => setForm({ ...form, name: v })} placeholder={t('supplier_name_placeholder') || 'e.g. ABC Wholesale'} placeholderTextColor="#999" />

              <Text fontSize={14} color="$colorSecondary" mb={4}>{t('contact_name') || 'Contact Name'}</Text>
              <TextInput style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12 }} value={form.contact_name} onChangeText={(v) => setForm({ ...form, contact_name: v })} placeholder={t('contact_name_placeholder') || 'e.g. John Smith'} placeholderTextColor="#999" />

              <XStack gap="$sm">
                <YStack f={1}>
                  <Text fontSize={14} color="$colorSecondary" mb={4}>{t('email') || 'Email'}</Text>
                  <TextInput style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12 }} value={form.email} onChangeText={(v) => setForm({ ...form, email: v })} placeholder="email@example.com" placeholderTextColor="#999" keyboardType="email-address" autoCapitalize="none" />
                </YStack>
                <YStack f={1}>
                  <Text fontSize={14} color="$colorSecondary" mb={4}>{t('phone') || 'Phone'}</Text>
                  <TextInput style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12 }} value={form.phone} onChangeText={(v) => setForm({ ...form, phone: v })} placeholder="+358..." placeholderTextColor="#999" keyboardType="phone-pad" />
                </YStack>
              </XStack>

              <Text fontSize={16} fontWeight="600" color="$color" mt="$sm" mb="$sm">{t('address') || 'Address'}</Text>
              <TextInput style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12 }} value={form.address_line1} onChangeText={(v) => setForm({ ...form, address_line1: v })} placeholder={t('street_address') || 'Street address'} placeholderTextColor="#999" />
              <XStack gap="$sm">
                <YStack f={1}>
                  <Text fontSize={14} color="$colorSecondary" mb={4}>{t('city') || 'City'}</Text>
                  <TextInput style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12 }} value={form.city} onChangeText={(v) => setForm({ ...form, city: v })} placeholder={t('city') || 'City'} placeholderTextColor="#999" />
                </YStack>
                <YStack f={1}>
                  <Text fontSize={14} color="$colorSecondary" mb={4}>{t('postal_code') || 'Postal Code'}</Text>
                  <TextInput style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12 }} value={form.postal_code} onChangeText={(v) => setForm({ ...form, postal_code: v })} placeholder="00100" placeholderTextColor="#999" />
                </YStack>
              </XStack>

              <Text fontSize={16} fontWeight="600" color="$color" mt="$sm" mb="$sm">{t('business_info') || 'Business Info'}</Text>
              <XStack gap="$sm">
                <YStack f={1}>
                  <Text fontSize={14} color="$colorSecondary" mb={4}>{t('business_id') || 'Business ID'}</Text>
                  <TextInput style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12 }} value={form.business_id} onChangeText={(v) => setForm({ ...form, business_id: v })} placeholder="1234567-8" placeholderTextColor="#999" />
                </YStack>
                <YStack f={1}>
                  <Text fontSize={14} color="$colorSecondary" mb={4}>{t('payment_terms') || 'Payment Terms'}</Text>
                  <TextInput style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12 }} value={form.payment_terms} onChangeText={(v) => setForm({ ...form, payment_terms: v })} placeholder="Net 30" placeholderTextColor="#999" />
                </YStack>
              </XStack>

              <Text fontSize={14} color="$colorSecondary" mb={4}>{t('notes') || 'Notes'}</Text>
              <TextInput style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12, height: 80, textAlignVertical: 'top' }} value={form.notes} onChangeText={(v) => setForm({ ...form, notes: v })} placeholder={t('notes_placeholder') || 'Any additional notes...'} placeholderTextColor="#999" multiline />
            </ScrollView>

            <YStack m="$md" bg={merchantColors.primary} br="$md" p="$md" ai="center" opacity={saving ? 0.6 : 1} onPress={saving ? undefined : saveSupplier} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
              {saving ? <Spinner color="#fff" /> : <Text color="white" fontSize={16} fontWeight="600">{t('save') || 'Save'}</Text>}
            </YStack>
      </Sheet>
    </FullScreenLayout>
  )
}
